import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../common/context/AuthContext';
import PermissionDenied from '../../common/components/PermissionDenied';
import { SALON_API_URL } from '../../api/config';
import { Lock, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';

const SHORT_MS = 30 * 60 * 1000;
const LONG_MS = 30 * 24 * 60 * 60 * 1000;
const LABEL = { sales: 'Sales Report', expenses: 'Expense Report', attendant: 'Attendant Report' };

function storageKey(reportKey) {
  return 'salon_report_access_' + reportKey;
}

function readSession(reportKey) {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(storageKey(reportKey));
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const ttl = parsed.remember ? LONG_MS : SHORT_MS;
      if (Date.now() - parsed.ts < ttl) return true;
      store.removeItem(storageKey(reportKey));
    } catch {}
  }
  return false;
}

function writeSession(reportKey, remember) {
  const data = JSON.stringify({ ts: Date.now(), remember });
  const store = remember ? localStorage : sessionStorage;
  store.setItem(storageKey(reportKey), data);
}

export default function SalonProtectedRoute({ reportKey, children }) {
  const navigate = useNavigate();
  const { userData, userToken: token } = useAuth();
  const [unlocked, setUnlocked] = useState(() => readSession(reportKey));
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [remember, setRemember] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const pinRefs = useRef([]);

  useEffect(() => {
    if (!unlocked) {
      setTimeout(() => pinRefs.current[0]?.focus(), 100);
    }
  }, [unlocked]);

  const handleVerify = async (pinValue) => {
    const pin = pinValue || pinDigits.join('');
    if (pin.length !== 4) {
      setErrorMsg('Please enter a 4-digit PIN');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const res = await fetch(`${SALON_API_URL}/salon-users/me/report-pin/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pin }),
      });

      const data = await res.json();

      if (data.success) {
        writeSession(reportKey, remember);
        setUnlocked(true);
      } else {
        setErrorMsg(data.message || 'Incorrect PIN. Access denied.');
        setPinDigits(['', '', '', '']);
        setTimeout(() => pinRefs.current[0]?.focus(), 50);
      }
    } catch (err) {
      setErrorMsg('Unable to verify PIN. Please try again.');
      setPinDigits(['', '', '', '']);
      setTimeout(() => pinRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...pinDigits];
    next[index] = value.slice(-1);
    setPinDigits(next);
    setErrorMsg('');

    if (value && index < 3) {
      pinRefs.current[index + 1]?.focus();
    }

    if (index === 3 && value && next.every(d => d)) {
      handleVerify(next.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const isManager = userData?.role === 'manager';
  const restrictedList = userData?.restrictedFeatures || [];
  const isReportRestricted = (reportKey === 'sales' && restrictedList.includes('sales_reports')) ||
                             (reportKey === 'expenses' && restrictedList.includes('expense_reports')) ||
                             (reportKey === 'attendant' && restrictedList.includes('analytics'));

  if (isManager && isReportRestricted) {
    return (
      <PermissionDenied
        variant="card"
        title="Access Denied"
        message="You do not have permission to open this report. Please contact your system administrator."
        onGoBack={() => navigate('/')}
      />
    );
  }

  if (unlocked) {
    return children;
  }

  const reportTitle = LABEL[reportKey] || 'Protected Report';

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-xl text-center space-y-6">
        
        {/* Header Icon */}
        <div className="w-16 h-16 rounded-2xl bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 flex items-center justify-center mx-auto">
          <Lock size={32} />
        </div>

        {/* Title */}
        <div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Protected Section
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Enter your Admin/Manager PIN to access the <span className="font-bold text-slate-700 dark:text-slate-200">{reportTitle}</span>.
          </p>
        </div>

        {/* PIN Inputs */}
        <div className="space-y-4">
          <div className="flex justify-center gap-3">
            {pinDigits.map((digit, i) => (
              <input
                key={i}
                ref={el => pinRefs.current[i] = el}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handlePinChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                disabled={loading}
                className="w-14 h-14 text-center text-2xl font-black border-2 rounded-2xl border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-pink-600 focus:bg-white dark:focus:bg-slate-900 focus:ring-4 focus:ring-pink-600/10 transition-all disabled:opacity-50"
              />
            ))}
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400">
              <ShieldAlert size={14} />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Remember Me Option */}
          <div className="flex items-center justify-center gap-2 pt-2">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                className="rounded border-slate-300 text-pink-600 focus:ring-pink-500"
              />
              Remember access on this device
            </label>
          </div>

          {/* Action Button */}
          <button
            onClick={() => handleVerify()}
            disabled={loading || pinDigits.some(d => !d)}
            className="w-full h-12 rounded-2xl bg-pink-600 text-white font-bold text-sm shadow-lg shadow-pink-600/20 hover:bg-pink-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Verifying PIN...</span>
              </>
            ) : (
              <>
                <ShieldCheck size={18} />
                <span>Unlock Access</span>
              </>
            )}
          </button>
        </div>

        {/* Footer Note */}
        <p className="text-[11px] text-slate-400">
          Only authorized Salon Admins &amp; Managers with valid PIN permissions can view report analytics.
        </p>

      </div>
    </div>
  );
}
